import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTestimonials, addTestimonial, updateTestimonial, deleteTestimonial, uploadTestimonialImage } from './services/firebaseService';
import Header from './Header';
import Footer from './Footer';
import AdminSidebar from './AdminSidebar';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

const PAGE_SIZE = 20;

const ManageTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    text: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(testimonials.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, testimonials.length);
  const paginatedTestimonials = testimonials.slice(startIndex, endIndex);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    setLoading(true);
    const result = await getTestimonials();
    if (result.success) {
      setTestimonials(result.testimonials);
    } else {
      setError('Failed to load testimonials: ' + result.error);
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setEditingTestimonial(null);
    setFormData({ text: '' });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({ text: testimonial.text || '' });
    setImageFile(null);
    setImagePreview(testimonial.image || '');
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      setLoading(true);
      const result = await deleteTestimonial(id);
      if (result.success) {
        await loadTestimonials();
        alert('Testimonial deleted successfully!');
      } else {
        alert('Failed to delete testimonial: ' + result.error);
      }
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.text.trim()) {
      alert('Please enter a testimonial message');
      return;
    }

    if (!imageFile && !imagePreview) {
      alert('Please upload a testimonial image');
      return;
    }

    setSubmitting(true);

    try {
      let imageUrl = imagePreview;

      if (imageFile) {
        const testimonialId = editingTestimonial?.id || null;
        imageUrl = await uploadTestimonialImage(imageFile, testimonialId);
      }

      if (!imageUrl) {
        alert('Please upload a testimonial image');
        setSubmitting(false);
        return;
      }

      const testimonialData = {
        text: formData.text.trim(),
        image: imageUrl
      };

      if (editingTestimonial) {
        const result = await updateTestimonial(editingTestimonial.id, testimonialData);
        if (result.success) {
          await loadTestimonials();
          setShowModal(false);
          alert('Testimonial updated successfully!');
        } else {
          alert('Failed to update testimonial: ' + result.error);
        }
      } else {
        const result = await addTestimonial(testimonialData);
        if (result.success) {
          await loadTestimonials();
          setShowModal(false);
          setFormData({ text: '' });
          setImageFile(null);
          setImagePreview('');
          alert('Testimonial added successfully!');
        } else {
          alert('Failed to add testimonial: ' + result.error);
        }
      }
    } catch (err) {
      console.error('Error saving testimonial:', err);
      alert('Failed to save testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    setFormData({ text: '' });
    setImageFile(null);
    setImagePreview('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="pt-20 flex flex-1">
        <AdminSidebar activeItem="testimonials" />
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4 border-b-2 border-gray-200 pb-4">
            <div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="flex items-center gap-1 text-gray-500 hover:text-primary transition-colors mb-2 text-sm"
              >
                <ArrowBackIcon className="!w-[18px] !h-[18px]" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Testimonials</h1>
            </div>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 bg-gray-200 text-primary py-2 px-4 rounded font-bold hover:opacity-90 transition-opacity self-start md:self-auto shadow-md hover:shadow-none hover:translate-y-px disabled:opacity-60"
              disabled={loading}
            >
              <AddCircleOutlineIcon className="!w-5 !h-5" />
              Add Testimonial
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded shadow-sm overflow-x-auto">
            {loading && (
              <div className="p-8 text-center">
                <div className="text-gray-500">Loading testimonials...</div>
              </div>
            )}
            {!loading && testimonials.length === 0 && (
              <div className="p-8 text-center">
                <div className="text-gray-500">No testimonials found. Add your first testimonial!</div>
              </div>
            )}
            {!loading && testimonials.length > 0 && (
              <>
                <table className="w-full text-left border-collapse min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-200">
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Image</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900">Message</th>
                      <th className="py-2 px-4 text-xs uppercase tracking-wider font-semibold text-gray-900 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-gray-900">
                    {paginatedTestimonials.map((testimonial, index) => (
                      <tr
                        key={testimonial.id}
                        className={`border-b border-gray-200 transition-colors hover:bg-gray-100 ${
                          index % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : ''
                        }`}
                      >
                        <td className="py-2 px-4">
                          {testimonial.image ? (
                            <img
                              src={testimonial.image}
                              alt="Testimonial"
                              className="w-16 h-16 object-cover border border-gray-200 rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-500 rounded">
                              No Image
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-4 max-w-md">
                          <p className="font-medium truncate">{testimonial.text}</p>
                        </td>
                        <td className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEdit(testimonial)}
                              className="text-gray-400 hover:text-primary transition-colors p-1"
                              title="Edit"
                              disabled={loading}
                            >
                              <EditIcon className="!w-5 !h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(testimonial.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              title="Delete"
                              disabled={loading}
                            >
                              <DeleteIcon className="!w-5 !h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="bg-white border-t border-gray-200 p-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500 pl-2">
                    Showing {startIndex + 1} to {endIndex} of {testimonials.length} entries
                  </span>
                  <div className="flex items-center gap-1 pr-2">
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((page) => page - 1)}
                      type="button"
                      aria-label="Previous page"
                    >
                      <ChevronLeftIcon className="!w-5 !h-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        className={`w-8 h-8 rounded text-sm font-semibold flex items-center justify-center ${
                          page === currentPage
                            ? 'bg-primary text-white'
                            : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        onClick={() => setCurrentPage(page)}
                        type="button"
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="p-1 text-gray-400 hover:text-primary disabled:opacity-50"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((page) => page + 1)}
                      type="button"
                      aria-label="Next page"
                    >
                      <ChevronRightIcon className="!w-5 !h-5" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleCloseModal}>
          <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl w-full mx-4 shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-3">
              <h2 className="text-xl font-bold text-primary">
                {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <CloseIcon className="!w-6 !h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Testimonial Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="text"
                  value={formData.text}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="border border-gray-200 rounded p-2 bg-white text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors text-sm w-full"
                  placeholder="Enter testimonial message"
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  Testimonial Image <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!imagePreview}
                  className="border border-gray-200 rounded p-2 bg-white text-sm w-full"
                  disabled={submitting}
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-24 h-24 object-cover border border-gray-200 rounded"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  className="px-4 py-2 border-2 border-primary text-primary text-sm font-bold rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-blue-900 transition-colors shadow-sm disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ManageTestimonials;
